/**
 * Set CSS theme colors from app configuration
 * This function sets CSS custom properties on the document root
 * so that colors can be dynamically changed based on environment variables
 */

import { appConfig } from '../config/appConfig';

/**
 * Generate color shades from a base color
 * This is a simplified version - for production, you might want to use a color library
 */
function generateColorShades(baseColor: string): Record<string, string> {
  // For now, we'll use the base color for all shades
  // In production, you might want to use a library like 'color' or 'chroma-js'
  // to generate proper shades
  return {
    50: baseColor + '0D', // 5% opacity
    100: baseColor + '1A', // 10% opacity
    200: baseColor + '33', // 20% opacity
    300: baseColor + '4D', // 30% opacity
    400: baseColor + '66', // 40% opacity
    500: baseColor, // Base color
    600: baseColor + 'CC', // 80% opacity (darker)
    700: baseColor + 'B3', // 70% opacity
    800: baseColor + '99', // 60% opacity
    900: baseColor + '80', // 50% opacity
    950: baseColor + '66', // 40% opacity
  };
}

/**
 * Set theme colors on the document root
 * This allows CSS to use these variables dynamically
 */
export function setThemeColors(): void {
  const root = document.documentElement;
  
  // Set primary and secondary colors as CSS variables
  // These will be used by the CSS file via var(--app-primary-500, fallback)
  root.style.setProperty('--app-primary-500', appConfig.primaryColor);
  root.style.setProperty('--app-secondary-500', appConfig.secondaryColor);
  root.style.setProperty('--app-accent-500', appConfig.accentColor);
  
  // Also set the primary-500 and secondary-500 directly for Tailwind compatibility
  root.style.setProperty('--primary-500', appConfig.primaryColor);
  root.style.setProperty('--secondary-500', appConfig.secondaryColor);
  
  // Log in development
  if (import.meta.env.DEV) {
    console.log('[setThemeColors] Applied theme colors:', {
      primary: appConfig.primaryColor,
      secondary: appConfig.secondaryColor,
      accent: appConfig.accentColor,
    });
  }
}

